import React, { useState, useEffect } from 'react'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Slider } from '@/shared/components/ui/slider'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useCharacters } from '@/features/characters/queries/character.queries'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import { AIShotGenerationCharacterVariantSelection, AIShotGenerationOptions, Moods, VisualStyles } from '@storyverse/shared'
import { AIGenerationBaseDialog } from '@/shared/components/custom/AIGenerationBaseDialog'
import ShotCharacterVariantPicker from './ShotCharacterVariantPicker'
import { SHOTLIST_MAX_CHARACTER_VARIANTS } from '../config/shotlist.config'

export interface ShotListGenerationOptions extends Omit<AIShotGenerationOptions, 'sceneContent'> { }

interface ShotListGenerationDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onGenerate: (options: ShotListGenerationOptions) => void
    isLoading?: boolean
}

export const ShotListGenerationDialog: React.FC<ShotListGenerationDialogProps> = ({
    open,
    onOpenChange,
    onGenerate,
    isLoading = false,
}) => {
    const [mode, setMode] = useState<'auto' | 'custom' | null>(null)
    const [shotCount, setShotCount] = useState<number>(10)
    const [mood, setMood] = useState('')
    const [visualStyle, setVisualStyle] = useState('')
    const [sceneDuration, setSceneDuration] = useState('')
    const [characterVariants, setCharacterVariants] = useState<AIShotGenerationCharacterVariantSelection[]>([])
    const { project } = useWorkspace()
    const { data: characters = [] } = useCharacters(project?.id ?? '')

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setMode(null)
            setShotCount(10)
            setMood('')
            setVisualStyle('')
            setSceneDuration('')
            setCharacterVariants([])
        }
    }, [open])

    const handleGenerate = (selectedMode: 'auto' | 'custom') => {
        onGenerate({
            mode: selectedMode,
            shotCount: selectedMode === 'custom' ? shotCount : undefined,
            mood: selectedMode === 'custom' ? mood : undefined,
            visualStyle: selectedMode === 'custom' ? visualStyle : undefined,
            sceneDuration: selectedMode === 'custom' ? sceneDuration : undefined,
            characterVariants: selectedMode === 'custom' ? characterVariants : undefined,
        })
    }

    const reset = () => {
        setMode(null)
        onOpenChange(false)
    }

    return (
        <AIGenerationBaseDialog
            open={open}
            onOpenChange={reset}
            dialogTitle="Generate Shot List"
            dialogDescription="Choose how you want the AI to generate the shot list for this scene."
            mode={mode}
            onModeChange={setMode}
            isLoading={isLoading}
            loadingTitle="Generating Shot List..."
            loadingDescription="Please wait while AI crafts the shots for your scene"
            autoPilotDescription="Let Storyverse AI decide the best shots, angles, and pacing automatically."
            onAutoPilotClick={() => handleGenerate('auto')}
            customDirectorDescription="You are in control. Specify shot count, mood, visual style, and pacing before rolling 'action'."
            customStepDescription="Fine-tune your shot list generation"
            onCustomGenerate={() => handleGenerate('custom')}
            customGenerateLabel="Generate Shot List"
        >
            <div className="space-y-4">
                <div className="space-y-3">
                    <div className="flex justify-between">
                        <Label>Approximate Shot Count</Label>
                        <span className="text-sm text-muted-foreground font-mono">{shotCount} shots</span>
                    </div>
                    <Slider
                        value={[shotCount]}
                        onValueChange={(v: number[]) => setShotCount(v[0])}
                        min={3}
                        max={30}
                        step={1}
                        className="w-full"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="mood">Mood</Label>
                        <Select value={mood} onValueChange={setMood}>
                            <SelectTrigger id="mood">
                                <SelectValue placeholder="Select mood" />
                            </SelectTrigger>
                            <SelectContent>
                                {Moods.map((m) => (
                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="style">Visual Style</Label>
                        <Select value={visualStyle} onValueChange={setVisualStyle}>
                            <SelectTrigger id="style">
                                <SelectValue placeholder="Select style" />
                            </SelectTrigger>
                            <SelectContent>
                                {VisualStyles.map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="duration">Estimated Scene Duration</Label>
                    <div className="relative">
                        <Input
                            id="duration"
                            placeholder="e.g. 2 minutes, 45 seconds"
                            value={sceneDuration}
                            onChange={(e) => setSceneDuration(e.target.value)}
                        />
                    </div>
                    <p className="text-[0.8rem] text-muted-foreground">Helps AI estimate pacing.</p>
                </div>

                <ShotCharacterVariantPicker
                    characters={characters}
                    selected={characterVariants}
                    onChange={setCharacterVariants}
                    maxSelections={SHOTLIST_MAX_CHARACTER_VARIANTS}
                />
            </div>
        </AIGenerationBaseDialog>
    )
}

export default ShotListGenerationDialog
